import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CountrySelect } from "@/components/profile/dropdowns/CountrySelect";
import { getDefaultCurrencyForCountry } from "@/constants/countries";
import { getCurrencyLabel } from "@/lib/currency";

interface CountryCurrencyPickerProps {
  country?: string;
  currency?: string; // ISO 4217 code
  onCountryChange?: (country: string) => void;
  onCurrencyChange?: (currency: string) => void;
  disabled?: boolean;
}

export function CountryCurrencyPicker({ country, currency, onCountryChange, onCurrencyChange, disabled }: CountryCurrencyPickerProps) {
  const { data: currencies } = useQuery({
    queryKey: ["currencies"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("currencies")
        .select("code,symbol,is_active")
        .eq("is_active", true)
        .order("code", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 1000 * 60 * 10,
  });

  const currencyOptions = useMemo(() => {
    const list = (currencies || []).slice().sort((a: any, b: any) => String(a.code).localeCompare(String(b.code)));
    const preferred = ["CAD", "USD"];
    const preferredItems = preferred
      .map((p) => list.find((c: any) => String(c.code).toUpperCase() === p))
      .filter(Boolean) as any[];
    const rest = list.filter((c: any) => !preferred.includes(String(c.code).toUpperCase()));
    return [...preferredItems, ...rest];
  }, [currencies]);

  // When country changes and currency is empty, choose a default currency if available in active list.
  useEffect(() => {
    if (!onCurrencyChange) return;
    if (!country) return;
    if (currency) return; // don't override explicit user selection
    const mapped = getDefaultCurrencyForCountry(country);
    const active = new Set((currencies || []).map((c: any) => c.code));
    const pick = mapped && active.has(mapped) ? mapped : undefined;
    if (pick) onCurrencyChange(pick);
  }, [country, currencies, currency, onCurrencyChange]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div>
        <CountrySelect value={country} onValueChange={(v) => onCountryChange?.(v)} placeholder="Select country" />
      </div>
      <div>
        <Select value={currency} onValueChange={(v) => onCurrencyChange?.(v)}>
          <SelectTrigger className="w-full h-9 text-sm" disabled={disabled}>
            <SelectValue placeholder="Select currency" />
          </SelectTrigger>
          <SelectContent>
            {currencyOptions.map((c: any) => (
              <SelectItem key={c.code} value={c.code}>
                {getCurrencyLabel(c.code)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
